import { useMemo, useState } from 'react';
import {
  sortCreditMonitorAccounts,
  type CreditMonitorAccountRow,
  type CreditMonitorFilter,
  type CreditMonitorSort,
} from '@nadoutong/contracts';
import { useCreditMonitor, useCreditOverview, useCreditOverdueHeartbeat } from '../api/credit.ts';
import { CreditBillingTimeline } from '../components/credit/CreditBillingTimeline.tsx';
import { CreditMonitorRow } from '../components/credit/CreditMonitorRow.tsx';
import { DualCurrencyAmount } from '../components/DualCurrencyAmount.tsx';
import { coerceErrorMessage } from '../components/Toast.tsx';
import { usdMicrosToCnyCents } from '../components/formatters.ts';
import { usePricing } from '../api/pricing.ts';
import { useT } from '../i18n/index.ts';
import {
  Button,
  Chip,
  EmptyState,
  KpiCard,
  PageHeader,
  Panel,
  Table,
  TableEmpty,
  TableSkeleton,
  TBody,
  Td,
  Th,
  THead,
  Tr,
} from '../ui/index.ts';

/**
 * Credit monitor (router#2014, expanding the #2007 overdue page).
 *
 * One page. Overdue rows stay pinned and cannot be dismissed; settlement
 * is the only exit. Expanding a customer feeds #2010's
 * CreditBillingTimeline — no simplified copy.
 *
 * KPIs and the overdue banner are the visible-scope FULL set (the
 * caller's book after #1955 narrowing). Filter/sort only cuts the
 * table. Labels say so; the table caption is not the KPI caption.
 *
 * No pagination this round. When a page is added: KPIs stay the
 * visible-scope full set (never the current page sum), and overdue
 * rows stay on page one.
 */

const FILTERS: readonly CreditMonitorFilter[] = [
  'all',
  'overdue',
  'due_soon',
  'temporary_expiring',
];

const SORTS: readonly CreditMonitorSort[] = ['default', 'usage', 'name'];

function filterLabelKey(
  filter: CreditMonitorFilter,
):
  | 'credit.monitor.filter.all'
  | 'credit.monitor.todo.overdue'
  | 'credit.monitor.todo.due_soon'
  | 'credit.monitor.todo.temporary_expiring' {
  if (filter === 'all') return 'credit.monitor.filter.all';
  if (filter === 'overdue') return 'credit.monitor.todo.overdue';
  if (filter === 'due_soon') return 'credit.monitor.todo.due_soon';
  return 'credit.monitor.todo.temporary_expiring';
}

function sortLabelKey(
  sort: CreditMonitorSort,
): 'credit.monitor.sort.default' | 'credit.monitor.sort.usage' | 'credit.monitor.sort.name' {
  if (sort === 'usage') return 'credit.monitor.sort.usage';
  if (sort === 'name') return 'credit.monitor.sort.name';
  return 'credit.monitor.sort.default';
}

function ExpandedTimeline({
  orgId,
  cnyPerUsd,
}: {
  readonly orgId: string;
  readonly cnyPerUsd: number | null;
}): JSX.Element {
  const t = useT();
  const overview = useCreditOverview(orgId, true);
  if (overview.isPending) {
    return <TableSkeleton rows={3} testId={`credit-monitor-timeline-loading-${orgId}`} />;
  }
  if (overview.isError || overview.data === undefined) {
    return (
      <EmptyState
        title={t('credit.overdue.page.load_error')}
        hint={overview.error === null || overview.error === undefined ? undefined : coerceErrorMessage(overview.error)}
        testId={`credit-monitor-timeline-error-${orgId}`}
      />
    );
  }
  return (
    <CreditBillingTimeline
      summary={overview.data.timeline.summary}
      entries={overview.data.timeline.entries}
      cnyPerUsd={cnyPerUsd}
      testId={`credit-billing-timeline-${orgId}`}
    />
  );
}

export function CreditOverduePage(): JSX.Element {
  const t = useT();
  const pricing = usePricing();
  const cnyPerUsd = pricing.data?.policy?.cny_per_usd ?? null;
  const monitor = useCreditMonitor(true);
  const heartbeat = useCreditOverdueHeartbeat(true);
  const [filter, setFilter] = useState<CreditMonitorFilter>('all');
  const [sort, setSort] = useState<CreditMonitorSort>('default');
  const [expandedAccountId, setExpandedAccountId] = useState<string | null>(null);

  const accounts = monitor.data?.accounts ?? [];
  const summary = monitor.data?.summary ?? null;
  const hb = heartbeat.data?.heartbeat ?? null;
  const visible = useMemo(
    () => sortCreditMonitorAccounts(accounts, { filter, sort }),
    [accounts, filter, sort],
  );

  return (
    <div className="flex flex-col gap-6" data-testid="credit-overdue-page">
      <PageHeader title={t('credit.overdue.page.title')} sub={t('credit.overdue.page.caption')} />
      {hb !== null ? (
        <Chip tone={hb.stale ? 'bad' : 'ok'} testId="credit-overdue-heartbeat">
          {hb.stale
            ? t('credit.overdue.heartbeat.stale')
            : t('credit.overdue.heartbeat.ok', { scanned: String(hb.scanned) })}
        </Chip>
      ) : null}
      {summary !== null ? (
        <>
          <div
            className={
              summary.overdue_account_count > 0
                ? 'rounded-lg border border-bad-line bg-bad-dim px-4 py-3 font-medium text-bad'
                : 'rounded-lg border border-line-soft bg-card px-4 py-3 text-paper-mid'
            }
            data-testid="credit-monitor-overdue-count"
            data-count={String(summary.overdue_account_count)}
          >
            <span className="inline-flex flex-wrap items-center gap-2">
              {t('credit.monitor.overdue_count', {
                count: String(summary.overdue_account_count),
              })}
              <DualCurrencyAmount
                display="cny"
                cnyCents={usdMicrosToCnyCents(summary.overdue_usd_micros, cnyPerUsd)}
                signed={false}
                align="start"
              />
            </span>
          </div>
          <div className="grid gap-3 sm:grid-cols-3">
            <KpiCard
              label={t('credit.monitor.kpi.total_limit')}
              value={
                <DualCurrencyAmount
                  display="cny"
                  cnyCents={usdMicrosToCnyCents(summary.total_limit_usd_micros, cnyPerUsd)}
                  signed={false}
                  align="start"
                />
              }
              testId="credit-monitor-kpi-total"
            />
            <KpiCard
              label={t('credit.monitor.kpi.used')}
              value={
                <DualCurrencyAmount
                  display="cny"
                  cnyCents={usdMicrosToCnyCents(summary.used_usd_micros, cnyPerUsd)}
                  signed={false}
                  align="start"
                />
              }
              testId="credit-monitor-kpi-used"
            />
            <KpiCard
              label={t('credit.monitor.kpi.overdue_total')}
              value={
                <DualCurrencyAmount
                  display="cny"
                  cnyCents={usdMicrosToCnyCents(summary.overdue_usd_micros, cnyPerUsd)}
                  signed={false}
                  align="start"
                />
              }
              tone={summary.overdue_account_count > 0 ? 'bad' : 'default'}
              testId="credit-monitor-kpi-overdue"
            />
          </div>
          <div className="flex flex-wrap gap-2" data-testid="credit-monitor-todos">
            <Button
              variant={filter === 'overdue' ? 'secondary' : 'ghost'}
              onClick={() => setFilter('overdue')}
              data-testid="credit-monitor-todo-overdue"
            >
              {t('credit.monitor.todo.overdue')} · {summary.overdue_account_count}
            </Button>
            <Button
              variant={filter === 'due_soon' ? 'secondary' : 'ghost'}
              onClick={() => setFilter('due_soon')}
              data-testid="credit-monitor-todo-due-soon"
            >
              {t('credit.monitor.todo.due_soon')} · {summary.due_soon_account_count}
            </Button>
            <Button
              variant={filter === 'temporary_expiring' ? 'secondary' : 'ghost'}
              onClick={() => setFilter('temporary_expiring')}
              data-testid="credit-monitor-todo-temporary"
            >
              {t('credit.monitor.todo.temporary_expiring')} · {summary.temporary_expiring_account_count}
            </Button>
          </div>
        </>
      ) : null}
      <Panel
        caption={t('credit.monitor.table.caption')}
        count={visible.length}
        testId="credit-overdue-panel"
        actions={
          <div className="flex items-center gap-2">
            {FILTERS.map((item) => (
              <Button
                key={item}
                size="sm"
                variant={filter === item ? 'secondary' : 'ghost'}
                onClick={() => setFilter(item)}
                data-testid={`credit-monitor-filter-${item}`}
              >
                {t(filterLabelKey(item))}
              </Button>
            ))}
            {SORTS.map((item) => (
              <Button
                key={item}
                size="sm"
                variant={sort === item ? 'secondary' : 'ghost'}
                onClick={() => setSort(item)}
                data-testid={`credit-monitor-sort-${item}`}
              >
                {t(sortLabelKey(item))}
              </Button>
            ))}
          </div>
        }
      >
        {monitor.isPending ? <TableSkeleton rows={4} testId="credit-overdue-loading" /> : null}
        {monitor.isError ? (
          <EmptyState
            title={t('credit.overdue.page.load_error')}
            hint={coerceErrorMessage(monitor.error)}
            action={<Button onClick={() => void monitor.refetch()}>{t('common.retry')}</Button>}
            testId="credit-overdue-error"
          />
        ) : null}
        {monitor.data !== undefined ? (
          <Table testId="credit-overdue-table" className="rounded-none border-0">
            <THead>
              <Tr interactive={false}>
                <Th>{t('credit.overdue.col.account')}</Th>
                <Th>{t('credit.monitor.col.layers')}</Th>
                <Th>{t('credit.monitor.col.status')}</Th>
                <Th>{t('credit.monitor.col.next_due')}</Th>
              </Tr>
            </THead>
            <TBody>
              {visible.length === 0 ? (
                <TableEmpty colSpan={4} testId="credit-overdue-empty">
                  {filter !== 'all' && accounts.length > 0
                    ? t('credit.monitor.table.empty_filtered')
                    : t('credit.overdue.page.empty')}
                </TableEmpty>
              ) : (
                visible.map((row) => (
                  <MonitorRowGroup
                    key={row.account_id}
                    row={row}
                    expanded={expandedAccountId === row.account_id}
                    cnyPerUsd={cnyPerUsd}
                    onToggle={() =>
                      setExpandedAccountId((current) =>
                        current === row.account_id ? null : row.account_id,
                      )
                    }
                  />
                ))
              )}
            </TBody>
          </Table>
        ) : null}
      </Panel>
    </div>
  );
}

function MonitorRowGroup({
  row,
  expanded,
  onToggle,
  cnyPerUsd,
}: {
  readonly row: CreditMonitorAccountRow;
  readonly expanded: boolean;
  readonly onToggle: () => void;
  readonly cnyPerUsd: number | null;
}): JSX.Element {
  return (
    <>
      <CreditMonitorRow
        row={row}
        expanded={expanded}
        onToggle={onToggle}
        cnyPerUsd={cnyPerUsd}
      />
      {expanded && row.org_id !== null ? (
        <Tr interactive={false} data-testid={`credit-monitor-expanded-${row.account_id}`}>
          <Td colSpan={4}>
            <ExpandedTimeline orgId={row.org_id} cnyPerUsd={cnyPerUsd} />
          </Td>
        </Tr>
      ) : null}
    </>
  );
}
